// src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  sendEmailVerification as firebaseSendEmailVerification,
  signOut,
  onIdTokenChanged,
  sendPasswordResetEmail,
  confirmPasswordReset as firebaseConfirmPasswordReset, // Alias the SDK function
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode, // Alias the SDK function
  applyActionCode as firebaseApplyActionCode, // <-- Alias the SDK function
} from 'firebase/auth';
import { app } from '../firebase'; // Assuming app is needed for getAuth(app)
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Get auth instance once when the provider mounts
  const auth = getAuth(app);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // Track loading state for auth

  // Basic Auth functions
  const signup = useCallback(
    (email, password) => createUserWithEmailAndPassword(auth, email, password),
    [auth],
  );
  const login = useCallback(
    (email, password) => signInWithEmailAndPassword(auth, email, password),
    [auth],
  ); // Corrected typo in previous comment (signInWithAndPassword -> signInWithEmailAndPassword)
  const loginWithCustomToken = useCallback(
    (customToken) => {
      return signInWithCustomToken(auth, customToken);
    },
    [auth],
  );
  const logout = useCallback(() => signOut(auth), [auth]);
  const sendEmailVerification = useCallback((user, actionCodeSettings) => {
    return firebaseSendEmailVerification(user, actionCodeSettings);
  }, []);
  const resetPassword = useCallback(
    (email) => sendPasswordResetEmail(auth, email),
    [auth],
  ); // sendPasswordResetEmail takes auth instance

  // --- Wrapped Password Reset / Action Code Functions ---

  // Wrapper for verifyPasswordResetCode (takes oobCode, returns Promise<ActionCodeInfo>)
  // Used to check a password reset code *before* confirming the reset.
  const verifyResetCode = useCallback(
    (oobCode) => {
      return firebaseVerifyPasswordResetCode(auth, oobCode);
    },
    [auth],
  );

  // Wrapper for confirmPasswordReset (takes oobCode, newPassword, returns Promise<void>)
  // Used to apply the new password using a valid password reset oobCode.
  const confirmResetPassword = useCallback(
    (oobCode, newPassword) => {
      return firebaseConfirmPasswordReset(auth, oobCode, newPassword);
    },
    [auth],
  );

  // --- NEW: Wrapper for applyActionCode (takes oobCode, returns Promise<void>) ---
  // Used to apply other action codes, like email verification or email change.
  const applyActionCode = useCallback(
    (oobCode) => {
      return firebaseApplyActionCode(auth, oobCode);
    },
    [auth], // Depends on the auth instance
  );
  // --- End NEW ---

  // Save user profile to backend (CORRECTED)
  const saveUserProfile = useCallback(async (userData) => {
    try {
      // Getting the current user and token inside the callback is robust
      const user = getAuth(app).currentUser;
      if (!user) throw new Error('User not authenticated');

      const token = await user.getIdToken(true); // Get fresh token

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/auth/api/saveUserLoginData`,
        userData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      console.log('[AuthContext] User profile saved successfully.');
    } catch (error) {
      console.error('[AuthContext] Error saving user profile:', error);
      // Decide if you want to set a global error state here from AuthContext
      // For now, just log and re-throw
      throw error; // Re-throw for handling in the calling component
    }
  }, []); // getAuth(app) and axios are stable

  // Effect to listen for Auth state changes (login/logout)
  useEffect(() => {
    console.log('[AuthContext] Setting up onIdTokenChanged listener.');
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      console.log(
        '[AuthContext] onIdTokenChanged triggered. User:',
        user ? user.uid : null,
      );
      setCurrentUser((prevUser) => {
        // Prevent unnecessary state updates if user hasn't fundamentally changed
        if (
          (prevUser &&
            user &&
            prevUser.uid === user.uid &&
            prevUser.emailVerified === user.emailVerified) || // Same user, same verification status
          (!prevUser && !user) // Both null
        ) {
          console.log(
            '[AuthContext] onIdTokenChanged: User identity/verification unchanged, skipping state update.',
          );
          return prevUser;
        }
        console.log(
          '[AuthContext] onIdTokenChanged: User state changed. Updating state.',
        );
        return user;
      });
      // Set authLoading to false *after* the initial auth state is determined
      setAuthLoading(false);
      if (user) {
        console.log(
          'AuthContext: currentUser updated. Email verified:',
          user.emailVerified,
        );
        // Note: Initial profile fetch should be handled in DataContext based on currentUser
      } else {
        console.log('AuthContext: No user is logged in.');
        // Note: DataContext should clear userProfile on logout
      }
    });
    return () => {
      console.log('[AuthContext] Cleaning up onIdTokenChanged listener.');
      unsubscribe(); // Unsubscribe when the provider unmounts
    };
  }, [auth, setCurrentUser]); // Depends on auth and setCurrentUser

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const value = useMemo(
    () => ({
      currentUser,
      signup,
      login,
      loginWithCustomToken,
      logout,
      sendEmailVerification,
      resetPassword, // Send reset email
      confirmResetPassword, // Confirm password reset with code
      verifyResetCode, // Verify reset code (for password reset codes)
      applyActionCode, // <-- Expose the new wrapped function (for email verification, etc.)
      saveUserProfile, // Save backend profile
      authLoading, // Authentication loading status
      // Add other Auth related state/functions here as needed
    }),
    [
      currentUser,
      signup,
      login,
      loginWithCustomToken,
      logout,
      sendEmailVerification,
      resetPassword,
      confirmResetPassword,
      verifyResetCode,
      applyActionCode, // <-- Include new wrapped functions
      saveUserProfile,
      authLoading,
    ],
  ); // Depend on all values included in the context

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
